import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { cn } from '@shared/utils/cn';

export interface JsonTreeProps {
  data: unknown;
  initiallyExpanded?: number;  // depth to expand initially
  search?: string;             // highlight search matches
  path?: string[];             // internal - current path in the tree
  maxDepth?: number;           // maximum depth to render
}

interface TreeNodeProps {
  value: unknown;
  keyName?: string;
  path: string[];
  depth: number;
  maxDepth: number;
  search: string;
  onCopy: (value: unknown, path: string[]) => void;
}

function highlightSearch(text: string, search: string): React.ReactNode {
  if (!search.trim()) return text;
  
  const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, i) => 
    regex.test(part) ? 
      <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">{part}</mark> : 
      part
  );
}

function TreeNode({ value, keyName, path, depth, maxDepth, search, onCopy }: TreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(depth < 2); // expand first 2 levels by default
  
  if (depth > maxDepth) {
    return (
      <div className="text-muted-foreground italic">
        [Max depth ({maxDepth}) reached]
      </div>
    );
  }

  const valueType = Array.isArray(value) ? 'array' : typeof value;
  
  if (value === null) {
    return (
      <div className="flex items-center gap-2">
        {keyName && <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>}
        <span className="text-orange-600 dark:text-orange-400">null</span>
      </div>
    );
  }
  
  if (value === undefined) {
    return (
      <div className="flex items-center gap-2">
        {keyName && <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>}
        <span className="text-orange-600 dark:text-orange-400">undefined</span>
      </div>
    );
  }

  if (valueType === 'string') {
    return (
      <div className="flex items-center gap-2">
        {keyName && <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>}
        <span className="text-green-600 dark:text-green-400">
          &quot;{highlightSearch(value as string, search)}&quot;
        </span>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={() => onCopy(value, path)}
          className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (valueType === 'number' || valueType === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        {keyName && <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>}
        <span className="text-purple-600 dark:text-purple-400">{String(value)}</span>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={() => onCopy(value, path)}
          className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (valueType === 'array') {
    const arr = value as unknown[];
    const isEmpty = arr.length === 0;
    
    return (
      <div>
        <div className="flex items-center gap-2 group">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-5 w-5 p-0"
            disabled={isEmpty}
          >
            {isEmpty ? (
              <span className="w-3 h-3" />
            ) : isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
          {keyName && (
            <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>
          )}
          <span className="text-gray-600 dark:text-gray-400">
            [{arr.length}]{isEmpty ? '' : isExpanded ? '' : ' […]'}
          </span>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onCopy(value, path)}
            className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
        {isExpanded && !isEmpty && (
          <div className="ml-4 border-l border-gray-200 dark:border-gray-700 pl-4 mt-1">
            {arr.map((item, index) => (
              <div key={index} className="mt-1">
                <TreeNode
                  value={item}
                  keyName={String(index)}
                  path={[...path, String(index)]}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  search={search}
                  onCopy={onCopy}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (valueType === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    const isEmpty = keys.length === 0;
    
    return (
      <div>
        <div className="flex items-center gap-2 group">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-5 w-5 p-0"
            disabled={isEmpty}
          >
            {isEmpty ? (
              <span className="w-3 h-3" />
            ) : isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
          {keyName && (
            <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>
          )}
          <span className="text-gray-600 dark:text-gray-400">
            {`{${keys.length}}`}{isEmpty ? '' : isExpanded ? '' : ' {…}'}
          </span>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onCopy(value, path)}
            className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
        {isExpanded && !isEmpty && (
          <div className="ml-4 border-l border-gray-200 dark:border-gray-700 pl-4 mt-1">
            {keys.map((key) => (
              <div key={key} className="mt-1">
                <TreeNode
                  value={obj[key]}
                  keyName={key}
                  path={[...path, key]}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  search={search}
                  onCopy={onCopy}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Fallback for other types (functions, symbols, etc.)
  return (
    <div className="flex items-center gap-2">
      {keyName && <span className="text-blue-600 dark:text-blue-400">{highlightSearch(keyName, search)}: </span>}
      <span className="text-gray-500 dark:text-gray-400 italic">
        [{valueType}] {String(value).slice(0, 50)}
      </span>
    </div>
  );
}

export function JsonTree({ 
  data, 
  initiallyExpanded = 2, 
  search = '', 
  path = [], 
  maxDepth = 10 
}: JsonTreeProps) {
  const normalizedSearch = search.trim().toLowerCase();
  
  const handleCopy = (value: unknown, valuePath: string[]) => {
    let textToCopy: string;
    
    if (valuePath.length === 0) {
      // Root value
      textToCopy = JSON.stringify(value, null, 2);
    } else {
      // Specific path value
      textToCopy = JSON.stringify(value, null, 2);
    }
    
    void navigator.clipboard?.writeText(textToCopy);
  };

  return (
    <div className="font-mono text-sm">
      <TreeNode
        value={data}
        path={path}
        depth={0}
        maxDepth={maxDepth}
        search={normalizedSearch}
        onCopy={handleCopy}
      />
    </div>
  );
}
